import type { CSSProperties } from "react";
import { PERIODI_RAPIDI, periodoCorrente, periodoRapido } from "../dateRange";
import { CHIPS, PAYMENT_CHIPS, withSelected, type ChipDef } from "./OrdiniFilterCard";
import { IconSearch } from "./icons";
import { BADGE_TONI } from "./statoToni";

/** Chip di stato del telefono: 36px, parola intera, niente maiuscolo. Acceso =
 *  pieno nel colore dello stato, spento = contorno. Stessi colori dei badge. */
function ChipStato({
  chip,
  on,
  onToggle,
  quanti,
}: {
  chip: ChipDef;
  on: boolean;
  onToggle: (id: string) => void;
  /** Ordini che entrano nel chip (redesign 2026-10-27). */
  quanti?: number;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={() => onToggle(chip.id)}
      style={{ "--tono": BADGE_TONI[chip.tono] } as CSSProperties}
      className={[
        "om-press inline-flex h-10 shrink-0 items-center gap-2 whitespace-nowrap rounded-full border pl-3 pr-3.5 text-[14px] font-semibold",
        on
          ? "border-[color:var(--tono)] bg-[color:var(--tono)] text-white"
          : "border-line-2 bg-card text-ink",
      ].join(" ")}
    >
      <span
        aria-hidden="true"
        className={[
          "h-2 w-2 shrink-0 rounded-full",
          on ? "bg-card" : "bg-[color:var(--tono)]",
        ].join(" ")}
      />
      {chip.label}
      {quanti !== undefined && (
        <span
          className={[
            "min-w-[1.5rem] rounded-full px-1.5 text-center text-[13px] font-bold tabular-nums",
            on ? "bg-white/20" : "bg-paper-2",
          ].join(" ")}
        >
          {quanti}
        </span>
      )}
    </button>
  );
}

/** Voce a spunta dentro il foglio filtri (fornitore, canale, periodo). */
function Opzione({
  label,
  on,
  onClick,
  radio = false,
}: {
  label: string;
  on: boolean;
  onClick: () => void;
  radio?: boolean;
}) {
  return (
    <button
      type="button"
      role={radio ? "radio" : "checkbox"}
      aria-checked={on}
      onClick={onClick}
      className={[
        "om-press inline-flex h-10 items-center rounded-full border-[1.5px] px-4 text-[14px]",
        on
          ? "border-ink bg-ink font-semibold text-on-ink"
          : "border-line-2 bg-card font-medium text-ink",
      ].join(" ")}
    >
      {label}
    </button>
  );
}

function Sezione({ titolo, children }: { titolo: string; children: React.ReactNode }) {
  return (
    <section className="border-b border-line py-4 last:border-b-0">
      <h3 className="mb-2.5 text-[15px] font-bold text-ink">{titolo}</h3>
      <div className="flex flex-wrap gap-2">{children}</div>
    </section>
  );
}

/** Testata della lista ordini sul telefono: ricerca a tutta riga col tasto
 *  Filtri accanto, e sotto la fila dei sei stati che scorre col pollice. */
export function OrdiniRicercaMobile({
  search,
  onSearchChange,
  activeChips,
  onChipToggle,
  filtriAttivi,
  onApriFiltri,
  conteggi,
}: {
  conteggi?: Readonly<Record<string, number>>;
  search: string;
  onSearchChange: (v: string) => void;
  activeChips: ReadonlySet<string>;
  onChipToggle: (id: string) => void;
  /** Filtri accesi DENTRO il foglio (non i sei stati qui sotto). */
  filtriAttivi: number;
  onApriFiltri: () => void;
}) {
  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-center gap-2">
        <label className="flex h-12 min-w-0 flex-1 items-center gap-2.5 rounded-full border border-line-2 bg-card pl-4 pr-1.5 focus-within:border-ink">
          <IconSearch className="h-[18px] w-[18px] shrink-0 fill-none stroke-current stroke-[1.8] text-ink [stroke-linecap:round] [stroke-linejoin:round]" />
          <input
            type="search"
            enterKeyHint="search"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Ordine, cliente, tracking, SKU"
            aria-label="Cerca ordini"
            className="min-w-0 flex-1 border-none bg-transparent text-[16px] text-ink outline-none placeholder:text-ink-3 [&::-webkit-search-cancel-button]:hidden"
          />
          {search && (
            <button
              type="button"
              aria-label="Pulisci ricerca"
              onClick={() => onSearchChange("")}
              className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-ink active:bg-paper-2"
            >
              <svg
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          )}
        </label>
        <button
          type="button"
          onClick={onApriFiltri}
          aria-label={filtriAttivi > 0 ? `Filtri, ${filtriAttivi} attivi` : "Filtri"}
          className={[
            "om-press relative grid h-12 w-12 shrink-0 place-items-center rounded-full border",
            filtriAttivi > 0 ? "border-ink bg-ink text-on-ink" : "border-line-2 bg-card text-ink",
          ].join(" ")}
        >
          <svg
            className="h-5 w-5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M4 6h16M7 12h10M10 18h4" />
          </svg>
          {filtriAttivi > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-accent-2 px-1 text-[11px] font-bold text-white ring-2 ring-paper">
              {filtriAttivi}
            </span>
          )}
        </button>
      </div>
      <div
        role="group"
        aria-label="Stato degli ordini"
        className="-mx-3 flex gap-2 overflow-x-auto px-3 pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {CHIPS.map((c) => (
          <ChipStato
            key={c.id}
            chip={c}
            on={activeChips.has(c.id)}
            onToggle={onChipToggle}
            quanti={conteggi?.[c.id]}
          />
        ))}
      </div>
    </div>
  );
}

/** Quanti filtri sono accesi nel foglio: pagamento, fornitore, canale e un
 *  periodo diverso dagli ultimi 30 giorni. Il numero sul tasto Filtri. */
export function contaFiltriFoglio(
  activeChips: ReadonlySet<string>,
  suppliers: ReadonlySet<string>,
  channels: ReadonlySet<string>,
  dateFrom: string,
  dateTo: string,
): number {
  const pagamento = PAYMENT_CHIPS.filter((c) => activeChips.has(c.id)).length;
  const periodo = periodoCorrente(dateFrom, dateTo) === "30g" ? 0 : 1;
  return pagamento + suppliers.size + channels.size + periodo;
}

/** Contenuto del foglio Filtri: tutto quello che sul computer sta nella
 *  plancia, in sezioni a tutta larghezza con voci da 40px. */
export function OrdiniFiltriFoglio({
  activeChips,
  onChipToggle,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  supplierOptions,
  selectedSuppliers,
  onSupplierFilterChange,
  channelOptions,
  selectedChannels,
  onChannelFilterChange,
  conteggi,
}: {
  conteggi?: Readonly<Record<string, number>>;
  activeChips: ReadonlySet<string>;
  onChipToggle: (id: string) => void;
  dateFrom: string;
  dateTo: string;
  onDateFromChange: (v: string) => void;
  onDateToChange: (v: string) => void;
  supplierOptions: { value: string; label: string }[];
  selectedSuppliers: ReadonlySet<string>;
  onSupplierFilterChange: (next: Set<string>) => void;
  channelOptions: { value: string; label: string }[];
  selectedChannels: ReadonlySet<string>;
  onChannelFilterChange: (next: Set<string>) => void;
}) {
  const corrente = periodoCorrente(dateFrom, dateTo);
  const alterna = (set: ReadonlySet<string>, v: string, done: (n: Set<string>) => void) => {
    const next = new Set(set);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    done(next);
  };
  return (
    <div>
      <Sezione titolo="Stato">
        {CHIPS.map((c) => (
          <ChipStato
            key={c.id}
            chip={c}
            on={activeChips.has(c.id)}
            onToggle={onChipToggle}
            quanti={conteggi?.[c.id]}
          />
        ))}
      </Sezione>
      <Sezione titolo="Fornitore e incasso">
        {PAYMENT_CHIPS.map((c) => (
          <ChipStato
            key={c.id}
            chip={c}
            on={activeChips.has(c.id)}
            onToggle={onChipToggle}
            quanti={conteggi?.[c.id]}
          />
        ))}
      </Sezione>
      <section className="border-b border-line py-4">
        <h3 className="mb-2.5 text-[15px] font-bold text-ink">Periodo</h3>
        <div role="radiogroup" aria-label="Periodo" className="flex flex-wrap gap-2">
          {PERIODI_RAPIDI.map((p) => (
            <Opzione
              key={p.id}
              radio
              label={p.label}
              on={corrente === p.id}
              onClick={() => {
                const d = periodoRapido(p.id);
                onDateFromChange(d.from);
                onDateToChange(d.to);
              }}
            />
          ))}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <label className="flex flex-col gap-1 text-[13px] font-semibold text-ink">
            Dal
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => onDateFromChange(e.target.value)}
              aria-label="Data inizio"
              className="h-11 rounded-xl border border-line-2 bg-card px-3 text-[15px] font-normal tabular-nums text-ink outline-none focus:border-ink"
            />
          </label>
          <label className="flex flex-col gap-1 text-[13px] font-semibold text-ink">
            Al
            <input
              type="date"
              value={dateTo}
              onChange={(e) => onDateToChange(e.target.value)}
              aria-label="Data fine"
              className="h-11 rounded-xl border border-line-2 bg-card px-3 text-[15px] font-normal tabular-nums text-ink outline-none focus:border-ink"
            />
          </label>
        </div>
      </section>
      <Sezione titolo="Fornitore">
        {withSelected(supplierOptions, selectedSuppliers).map((o) => (
          <Opzione
            key={o.value}
            label={o.label}
            on={selectedSuppliers.has(o.value)}
            onClick={() => alterna(selectedSuppliers, o.value, onSupplierFilterChange)}
          />
        ))}
      </Sezione>
      <Sezione titolo="Canale">
        {withSelected(channelOptions, selectedChannels).map((o) => (
          <Opzione
            key={o.value}
            label={o.label}
            on={selectedChannels.has(o.value)}
            onClick={() => alterna(selectedChannels, o.value, onChannelFilterChange)}
          />
        ))}
      </Sezione>
    </div>
  );
}
